import { Layer, ZIndexes } from "@common/constants";
import { MUSEUM_ATTIC } from "@common/definitions/buildings";

/*
 * The museum attic's dark, without the drawing (client/src/scripts/managers/atticDark.ts draws it): its numbers, its
 * lights and their flicker, and how dark it is at a point, for what's drawn over it but gives no light of its own (a
 * smoke cloud, dust, splinters: 1.04.2's audit, they showed at full brightness in a black room). Kept free of the
 * page's drawing so the tests can run it.
 */

/** the dark's place among what's in the attic: over the boards, what's on them and the spiders; under the loot, a
 * grenade on the floor, the players and the bulbs */
export const ATTIC_DARK_ZINDEX = ZIndexes.ObstaclesLayer1 + 0.5;
/** a light's place up there: over the dark, which it lights (an explosion's flash, 1.04.2; the death markers, which
 * have to be seen), under the loot */
export const ATTIC_LIGHT_ZINDEX = ATTIC_DARK_ZINDEX + 0.1;

/** the attic's floor inside its walls (hall units), and the canvas's pixels a unit */
export const HALF_W = 128.5;
export const HALF_H = 93.5;
export const RES = 4;
/** the dark between the lights, and under a light at its brightest */
export const DARK = 0.93;
export const LIT = 0.3;
/** a light's pool (units): a bulb's, the candles' */
export const BULB_POOL = 15;
export const CANDLE_POOL = 8.5;
/** the glow round you: how far it reaches (units), and how much of the dark it takes away at your feet */
export const GLOW_REACH = 13;
export const GLOW = 0.6;
/** a light out: its pool covered back to the dark round it (the blob over it at its fullest) */
export const LIGHT_OUT = (DARK - LIT) / (1 - LIT);

export interface Light {
    readonly x: number
    readonly y: number
    readonly pool: number
    readonly candle: boolean
}

export const LIGHTS: readonly Light[] = [
    ...MUSEUM_ATTIC.lamps.map(([x, y]) => ({ x, y, pool: BULB_POOL, candle: false })),
    ...MUSEUM_ATTIC.candles.map(([x, y]) => ({ x, y, pool: CANDLE_POOL, candle: true }))
];

/** a bulb's brightness now, 0 to 1: a hum of flicker, now and then a stutter, the bad one (the second) often out */
export const bulbNoise = (seed: number, t: number): number => {
    const hum = 0.9 + 0.06 * Math.sin(t * 0.017 + seed * 3.1) + 0.04 * Math.sin(t * 0.051 + seed * 7.3);
    const bad = seed === 1;
    // (a stutter: the time cut into slots, each slot's chance of going dark its own)
    const slot = Math.floor(t / (bad ? 90 : 140));
    const roll = Math.abs(Math.sin(slot * 12.9898 + seed * 78.233) * 43758.5453) % 1;
    if (roll < (bad ? 0.4 : 0.04)) return roll < (bad ? 0.18 : 0.014) ? 0.02 : 0.35;
    return hum;
};

/** the candles' brightness now: never out, a quick waver and a slower breath */
export const candleNoise = (seed: number, t: number): number =>
    0.78 + 0.1 * Math.sin(t * 0.031 + seed) + 0.08 * Math.sin(t * 0.083 + seed * 2.3) + 0.04 * Math.sin(t * 0.21 + seed * 5.1);

/** each light's brightness now (0 out, 1 full), in LIGHTS' order */
export const flickerAt = (t: number): number[] => LIGHTS.map((light, i) => light.candle ? candleNoise(i, t) : bulbNoise(i, t));

/** how a bulb's picture is shaded for its brightness now (its glow is baked in: out, it goes dim with its pool) */
export const bulbShade = (brightness: number): number => 0.28 + 0.72 * Math.min(1, Math.max(0, brightness));

/** a radial gradient's alpha at `d` of its radius, from its stops (as the canvas draws them: straight between) */
const along = (stops: ReadonlyArray<readonly [number, number]>, d: number): number => {
    if (d <= stops[0][0]) return stops[0][1];
    for (let i = 1; i < stops.length; i++) {
        const [d1, a1] = stops[i];
        if (d <= d1) {
            const [d0, a0] = stops[i - 1];
            return a0 + (a1 - a0) * (d - d0) / (d1 - d0);
        }
    }
    return stops[stops.length - 1][1];
};
const POOL_STOPS = [[0, 1], [0.45, 0.6], [1, 0]] as const;
const GLOW_STOPS = [[0, 1], [0.35, 0.75], [0.7, 0.25], [1, 0]] as const;

/** whether a point (the attic's own units) is over its boards, inside its walls */
export const inAtticArea = (x: number, y: number, margin = 0): boolean => Math.abs(x) <= HALF_W + margin && Math.abs(y) <= HALF_H + margin;

/**
 * How dark the attic's dark is at a point (its own units), 0 to 1, as it's drawn (atticDark.ts): the dark, cut under
 * each light (to LIT at its middle), each light's flicker now covering its pool back, the glow round you erased; none
 * over the well. (The rafters' thin shadows are left out.) `flicker`: each light's brightness now (flickerAt); `you`:
 * the glow's middle and its strength, if it's shown
 */
export const darknessAt = (
    x: number,
    y: number,
    flicker: readonly number[],
    you?: { readonly x: number, readonly y: number, readonly glow: number },
    flashes: ReadonlyArray<{ readonly x: number, readonly y: number, readonly radius: number, readonly strength: number }> = []
): number => {
    if (!inAtticArea(x, y)) return 0;
    const [wx0, wy0, wx1, wy1] = MUSEUM_ATTIC.well;
    if (x > wx0 && x < wx1 && y > wy0 && y < wy1) return 0;
    let alpha = DARK;
    const cut = 1 - LIT / DARK;
    LIGHTS.forEach(({ x: lx, y: ly, pool }) => {
        const d = Math.hypot(x - lx, y - ly) / pool;
        if (d < 1) alpha *= 1 - cut * along(POOL_STOPS, d);
    });
    LIGHTS.forEach(({ x: lx, y: ly, pool }, i) => {
        const d = Math.hypot(x - lx, y - ly) / pool;
        if (d >= 1) return;
        const blob = (1 - (flicker[i] ?? 1)) * LIGHT_OUT * along(POOL_STOPS, d);
        alpha = blob + alpha * (1 - blob);
    });
    if (you) {
        const d = Math.hypot(x - you.x, y - you.y) / GLOW_REACH;
        if (d < 1) alpha *= 1 - you.glow * along(GLOW_STOPS, d);
    }
    for (const { x: fx, y: fy, radius, strength } of flashes) {
        const d = Math.hypot(x - fx, y - fy) / radius;
        if (d < 1) alpha *= 1 - strength * along(GLOW_STOPS, d);
    }
    return Math.min(1, Math.max(0, alpha));
};

/** `tint` (0xRRGGBB) darkened to `brightness` (0 to 1) */
export const shadeTint = (tint: number, brightness: number): number => {
    const k = Math.min(1, Math.max(0, brightness));
    const channel = (shift: number): number => Math.round(((tint >> shift) & 0xff) * k) << shift;
    return (channel(16) | channel(8) | channel(0)) >>> 0;
};

/** whether a particle's frames are a light's (a flame, an ember, a flash, a glow, a spark, the plasma): not shaded */
export const emissiveFrames = (frames: string | readonly string[]): boolean =>
    (typeof frames === "string" ? [frames] : frames).some(frame => /glow|ember|flame|fire|flash|spark|plasma|muzzle/.test(frame));

/**
 * whether the dark shows for a player on `layer`, `inside` the attic's footprint: up in it or on its stairs; or just off
 * them, while the attic's floor (`floor`, its container) is still fading out (1.04.2's audit: it went the moment you
 * stepped down, and the attic showed lit for a fifth of a second)
 */
export const darkShows = (layer: Layer, inside: boolean, floor: { readonly visible: boolean, readonly alpha: number }): boolean =>
    inside && (layer === Layer.Upstairs || layer === Layer.ToUpstairs || (floor.visible && floor.alpha > 0));
